/**
 * The module registry — the one list the dashboard renders and the shell routes
 * from. Adding a third module is an entry here plus its own folder; nothing in
 * one module imports another.
 *
 * This is metadata only (label, path, tile). Each module's routes are mounted in
 * `App.tsx`, and each module owns its own screens, storage and sync.
 */

export interface ModuleDef {
  id: string;
  /** The module's home path; the dashboard tile links here. */
  path: string;
  label: string;
  /** Tile accent, as a CSS colour. */
  accent: string;
  icon: React.ReactNode;
  /** Shown only to the member with this display name (case-insensitive). Absent: everyone. */
  onlyFor?: string;
  /** false: no dashboard tile — the route and sync keep working. */
  tile?: boolean;
}

/** Whether `m` is on this member's dashboard. */
export function moduleVisible(m: ModuleDef, displayName: string | null | undefined): boolean {
  return !m.onlyFor || (!!displayName && displayName.trim().toLowerCase() === m.onlyFor.toLowerCase());
}

function BasketIcon() {
  return (
    <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M4 9h16l-1.4 9.3a2 2 0 0 1-2 1.7H7.4a2 2 0 0 1-2-1.7L4 9Z" />
      <path d="M8 9 10.5 4M16 9 13.5 4M9 13v3M15 13v3" />
    </svg>
  );
}

function CalendarIcon() {
  return (
    <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <rect x="3.5" y="5" width="17" height="15" rx="2.5" />
      <path d="M3.5 9.5h17M8 3.5v3M16 3.5v3M7.5 13h3M13.5 13h3M7.5 16.5h3" />
    </svg>
  );
}

function ChecklistIcon() {
  return (
    <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M4 6.5l1.6 1.6L8.5 5M4 12.5l1.6 1.6L8.5 11M4 18.5l1.6 1.6L8.5 17" />
      <path d="M12 6.5h8M12 12.5h8M12 18.5h8" />
    </svg>
  );
}

function ChefHatIcon() {
  return (
    <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M6 14.5V19a1 1 0 0 0 1 1h10a1 1 0 0 0 1-1v-4.5" />
      <path d="M6 14.5a3.5 3.5 0 0 1-1.2-6.8 4.2 4.2 0 0 1 8-1.6 4.2 4.2 0 0 1 5.4 1.6A3.5 3.5 0 0 1 18 14.5H6Z" />
      <path d="M9.5 20v-4M14.5 20v-4" />
    </svg>
  );
}

function CrateIcon() {
  return (
    <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      {/* A produce crate, with a leafy sprout above the rim. */}
      <path d="M4 10.5h16l-1 8a2 2 0 0 1-2 1.7H7a2 2 0 0 1-2-1.7l-1-8Z" />
      <path d="M3.5 10.5 5 7h14l1.5 3.5M9 14v3M15 14v3" />
      <path d="M12 10.5c0-2 .8-3.6 2.4-4.6M12 10.5c-.4-1.6-1.4-2.8-3-3.4" />
    </svg>
  );
}

function WordleIcon() {
  return (
    <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      {/* A little board — two tiles guessed, two still open. */}
      <rect x="3" y="4" width="7" height="7" rx="1.6" />
      <rect x="14" y="4" width="7" height="7" rx="1.6" fill="currentColor" stroke="none" />
      <rect x="3" y="14" width="7" height="7" rx="1.6" fill="currentColor" stroke="none" />
      <rect x="14" y="14" width="7" height="7" rx="1.6" />
    </svg>
  );
}

function ChessIcon() {
  return (
    <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      {/* A knight's head on a base. */}
      <path d="M8 20h9M7.5 17h10" />
      <path d="M9 17c0-3 2.4-4.4 2.6-6.6L9.8 11.6 7.6 10.4 11 5.6c.8-1.2 2-1.6 3.2-1.2 2.6.9 3.8 4 3.4 7.6-.3 2.2-.9 3.6-.9 5" />
      <circle cx="13.6" cy="7.6" r="0.6" fill="currentColor" stroke="none" />
    </svg>
  );
}

function MorseIcon() {
  return (
    <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      {/* Dit-dah-dit on a sound wave: R in morse. */}
      <circle cx="4.5" cy="12" r="1.3" fill="currentColor" stroke="none" />
      <path d="M8.5 12h7" strokeWidth="2.6" />
      <circle cx="19.5" cy="12" r="1.3" fill="currentColor" stroke="none" />
      <path d="M6 6.5c3.6-2.6 8.4-2.6 12 0M6 17.5c3.6 2.6 8.4 2.6 12 0" />
    </svg>
  );
}

function ChatIcon() {
  return (
    <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      {/* A speech bubble with a little face: Huub, in the group chat. */}
      <path d="M5 4.5h14a2 2 0 0 1 2 2v8.5a2 2 0 0 1-2 2h-8l-4.5 3.5V17H5a2 2 0 0 1-2-2V6.5a2 2 0 0 1 2-2Z" />
      <circle cx="9" cy="10.5" r="0.9" fill="currentColor" stroke="none" />
      <circle cx="15" cy="10.5" r="0.9" fill="currentColor" stroke="none" />
      <path d="M9.5 13.5c1.4 1 3.6 1 5 0" />
    </svg>
  );
}

export const MODULES: ModuleDef[] = [
  {
    id: 'boodschappen',
    path: '/boodschappen',
    label: 'Boodschappen',
    accent: 'var(--accent)',
    icon: <BasketIcon />,
  },
  {
    // No tile (household request, 2026-10-30): the shared Google Calendar on
    // both phones is the agenda now. The module stays as its mirror — calendar
    // sync and Huub read and write it (docs/CALENDAR_SYNC.md) — and /agenda
    // still opens for anyone who goes looking.
    id: 'agenda',
    path: '/agenda',
    label: 'Agenda',
    accent: '#4b6bfb',
    icon: <CalendarIcon />,
    tile: false,
  },
  {
    id: 'taken',
    path: '/taken',
    label: 'Taken',
    accent: '#0ea472',
    icon: <ChecklistIcon />,
  },
  {
    id: 'recepten',
    path: '/recepten',
    label: 'Recepten',
    accent: '#e8590c',
    icon: <ChefHatIcon />,
  },
  {
    id: 'pakket',
    path: '/pakket',
    label: 'Pakket',
    accent: '#5c940d',
    icon: <CrateIcon />,
  },
  {
    id: 'woordje',
    path: '/woordje',
    label: 'Het Woord',
    accent: '#7048e8',
    icon: <WordleIcon />,
  },
  {
    // Jurren's own morse course (household request, 2026-10-13): hidden from
    // every other member — the tile, the route and the sync loop all ask here.
    // Matched on the display name, which is how the household says who. It hides
    // the module; it is not a security boundary (the rows are one learner's
    // letter progress, read back filtered to their own `created_by`).
    id: 'morse',
    path: '/morse',
    label: 'Morse',
    accent: '#0b7285',
    icon: <MorseIcon />,
    onlyFor: 'Jurren',
  },
  {
    // Jurren's own chess trainer (docs/SCHAAK.md, 2026-11-03): personal, like
    // Morse — the tile, the route and the sync loop all ask here.
    id: 'schaak',
    path: '/schaak',
    label: 'Schaak',
    accent: '#8a5a2b',
    icon: <ChessIcon />,
    onlyFor: 'Jurren',
  },
  {
    // Huub, the Signal assistant (docs/ASSISTENT.md §11): pairing, memory and
    // switches. Online-only server state, so no sync loop is started for it.
    id: 'huub',
    path: '/huub',
    label: 'Huub',
    accent: '#c2255c',
    icon: <ChatIcon />,
  },
];

/** Whether the module with this id is visible to this member. */
export function canSeeModule(id: string, displayName: string | null | undefined): boolean {
  const m = MODULES.find((x) => x.id === id);
  return !!m && moduleVisible(m, displayName);
}
